import { ClientRect, getClientRect, MeasuringConfiguration, Modifier } from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { getAppZoom } from "./screenScale";

/**
 * dnd-kit support for the automatic screen scale (helpers/screenScale.ts).
 *
 * dnd-kit measures with getBoundingClientRect (on-screen px) and renders transforms as
 * CSS px, which the zoomed document scales again, so dragged items trail the cursor.
 * We measure everything in layout px and scale the pointer delta to match, which keeps
 * rendering, sortable shifting and rect-based collision detection consistent.
 */

const toLayoutRect = (rect: ClientRect): ClientRect => {
  const zoom = getAppZoom();
  if (zoom === 1) return rect;
  return {
    top: rect.top / zoom,
    left: rect.left / zoom,
    bottom: rect.bottom / zoom,
    right: rect.right / zoom,
    width: rect.width / zoom,
    height: rect.height / zoom,
  };
};

const measureIgnoringTransform = (element: HTMLElement) =>
  toLayoutRect(getClientRect(element, { ignoreTransform: true }));

export const scaledDndMeasuring: MeasuringConfiguration = {
  draggable: { measure: measureIgnoringTransform },
  droppable: { measure: measureIgnoringTransform },
  dragOverlay: { measure: (element) => toLayoutRect(getClientRect(element)) },
};

/** Converts the pointer delta (on-screen px) to layout px. Keep it first in `modifiers`. */
export const scaleDragTransform: Modifier = ({ transform }) => {
  const zoom = getAppZoom();
  if (zoom === 1) return transform;
  return { ...transform, x: transform.x / zoom, y: transform.y / zoom };
};

/** `restrictToParentElement`, with the parent measured in layout px like everything else. */
export const restrictToParentElementScaled: Modifier = (args) =>
  restrictToParentElement({
    ...args,
    containerNodeRect: args.containerNodeRect && toLayoutRect(args.containerNodeRect),
  });
